import {
  Box,
  Boxes,
  Bell,
  ChevronDown,
  CircleGauge,
  ClipboardList,
  FileClock,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Link2,
  LogOut,
  Menu,
  PackagePlus,
  PanelsTopLeft,
  Search,
  ShieldCheck,
  Users,
  Workflow,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type PropsWithChildren } from "react";

import { api, jsonBody } from "../api";
import { useAuth } from "../auth";
import { Link, useNavigate, usePathname } from "../router";
import type { AdminNotification } from "../types";
import { relativeTime } from "./ui";

const buyerNav = [
  { icon: LayoutDashboard, label: "Overview", to: "/dashboard" },
  { icon: KeyRound, label: "My licenses", to: "/licenses" },
  { icon: PackagePlus, label: "Add license", to: "/add-license" },
];

const adminNav = [
  { icon: CircleGauge, label: "Operations", to: "/admin" },
  { icon: ClipboardList, label: "Licenses", to: "/admin/licenses" },
  { icon: Boxes, label: "Products", to: "/admin/products" },
  { icon: Users, label: "Buyers", to: "/admin/buyers" },
  { icon: ShieldCheck, label: "Access requests", to: "/admin/access" },
  { icon: PanelsTopLeft, label: "Discord panel", to: "/admin/discord-panel" },
  { icon: LifeBuoy, label: "Ticket panel", to: "/admin/ticket-panel" },
  { icon: FileClock, label: "Audit log", to: "/admin/audit" },
  { icon: Link2, label: "BuiltByBit", to: "/admin/builtbybit" },
  { icon: Workflow, label: "Discord operations", to: "/admin/operations" },
];

export function AppShell({ children }: PropsWithChildren) {
  const { account, config, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [density, setDensity] = useState(() => localStorage.getItem("ocloud-admin-density") || "comfortable");
  const pathname = usePathname();

  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem("ocloud-admin-density", density);
  }, [density]);
  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setNotificationOpen(false);
        setProfileOpen(false);
        setPaletteOpen(true);
      }
      if (event.key === "Escape") {
        setNotificationOpen(false);
        setProfileOpen(false);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);
  useEffect(() => {
    const closeFloatingMenus = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest(".notification-center")) setNotificationOpen(false);
      if (!target?.closest(".profile")) setProfileOpen(false);
    };
    document.addEventListener("pointerdown", closeFloatingMenus);
    return () => document.removeEventListener("pointerdown", closeFloatingMenus);
  }, []);
  useEffect(() => {
    setNotificationOpen(false);
    setProfileOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!account?.isAdmin) return;
    void api<AdminNotification[]>("/v1/web/admin/notifications").then(setNotifications).catch(() => undefined);
  }, [account?.isAdmin, pathname]);
  useEffect(() => {
    if (!account?.isAdmin || !pathname.startsWith("/admin")) return;
    const existing = JSON.parse(localStorage.getItem("ocloud-recent-admin") || "[]") as string[];
    localStorage.setItem("ocloud-recent-admin", JSON.stringify([pathname, ...existing.filter((item) => item !== pathname)].slice(0, 6)));
  }, [account?.isAdmin, pathname]);

  async function signOut() {
    await logout();
    navigate("/login", { replace: true });
  }

  const displayName = account?.displayName || account?.email || "Account";
  return (
    <div className="app-shell">
      <button
        aria-label="Close navigation"
        className={`shell-scrim ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
        type="button"
      />
      <aside className={`sidebar ${menuOpen ? "is-open" : ""}`}>
        <div className="sidebar__brand">
          <img alt="" src="/brand/ocloud-mark.png" />
          <div>
            <strong>oCloud License</strong>
            <span>Studio operations</span>
          </div>
          <button className="sidebar__close" onClick={() => setMenuOpen(false)} type="button">
            <X size={19} />
            <span className="sr-only">Close navigation</span>
          </button>
        </div>

        <nav aria-label="Buyer navigation" className="sidebar__nav">
          <span className="nav-label">Workspace</span>
          {buyerNav.map((item) => (
            <ShellNavLink
              key={item.to}
              onClick={() => setMenuOpen(false)}
              to={item.to}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </ShellNavLink>
          ))}
          {account?.isAdmin && (
            <>
              <span className="nav-label nav-label--spaced">Administration</span>
              {adminNav.map((item) => (
                <ShellNavLink
                  end={item.to === "/admin"}
                  key={item.to}
                  onClick={() => setMenuOpen(false)}
                  to={item.to}
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </ShellNavLink>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar__foot">
          {config?.supportUrl && (
            <a className="nav-item" href={config.supportUrl} rel="noreferrer" target="_blank">
              <LifeBuoy size={18} />
              <span>Support</span>
            </a>
          )}
          <div className="brand-pixels" aria-hidden="true"><i /><i /><i /><i /></div>
        </div>
      </aside>

      <div className="shell-content">
        <header className="topbar">
          <button className="topbar__menu" onClick={() => setMenuOpen(true)} type="button">
            <Menu size={20} />
            <span className="sr-only">Open navigation</span>
          </button>
          <div className="topbar__identity">
            <Box size={18} />
            <span>{breadcrumb(pathname)}</span>
          </div>
          {account?.isAdmin && <button className="command-trigger" onClick={() => setPaletteOpen(true)} type="button"><Search size={16} /><span>Search workspace</span><kbd>Ctrl K</kbd></button>}
          {account?.isAdmin && <div className="notification-center"><button aria-expanded={notificationOpen} aria-haspopup="menu" aria-label="Open notifications" className="topbar-icon" onClick={() => setNotificationOpen((value) => !value)} type="button"><Bell size={18} />{notifications.some((item) => !item.readAt) && <i />}</button>{notificationOpen && <NotificationMenu items={notifications} onChanged={setNotifications} />}</div>}
          <div className="profile">
            <button
              aria-expanded={profileOpen}
              className="profile__trigger"
              onClick={() => setProfileOpen((value) => !value)}
              type="button"
            >
              {account?.avatarUrl
                ? <img alt="" src={account.avatarUrl} />
                : <span className="avatar-fallback">{displayName.slice(0, 1).toUpperCase()}</span>}
              <span className="profile__copy">
                <strong>{displayName}</strong>
                <small>{account?.isAdmin ? "Administrator" : account?.discordUserId ? "Discord owner" : "Buyer account"}</small>
              </span>
              <ChevronDown size={15} />
            </button>
            {profileOpen && (
              <div className="profile__menu">
                <div>
                  <strong>{displayName}</strong>
                  <span>{account?.email || (account?.discordUserId ? `Discord ${account.discordUserId}` : "oCloud account")}</span>
                </div>
                <button onClick={() => void signOut()} type="button">
                  <LogOut size={16} /> Sign out
                </button>
                {account?.isAdmin && <button className="profile__utility" onClick={() => setDensity((value) => value === "compact" ? "comfortable" : "compact")} type="button"><PanelsTopLeft size={16} /> {density === "compact" ? "Comfortable density" : "Compact density"}</button>}
              </div>
            )}
          </div>
        </header>
        <main className="main-content">
          {children}
        </main>
      </div>
      {account?.isAdmin && <CommandPalette onClose={() => setPaletteOpen(false)} onNavigate={(to) => { setPaletteOpen(false); navigate(to); }} open={paletteOpen} />}
    </div>
  );
}

interface SearchResults {
  buyers: Array<{ id: string; displayName: string | null; email: string | null; discordUserId: string | null }>;
  licenses: Array<{ id: string; keyPrefix: string; status: string; product: { name: string }; discordLicense: { userId: string } | null }>;
  links: Array<{ id: string; buyerUsername: string; buyerUserId: string; discordUserId: string | null; status: string }>;
  products: Array<{ id: string; name: string; slug: string }>;
}

function CommandPalette({ onClose, onNavigate, open }: { onClose(): void; onNavigate(to: string): void; open: boolean }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const recent = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("ocloud-recent-admin") || "[]") as string[]; }
    catch { return []; }
  }, [open]);
  useEffect(() => {
    if (!open || query.trim().length < 2) { setResults(null); return; }
    const timer = window.setTimeout(() => void api<SearchResults>(`/v1/web/admin/search?q=${encodeURIComponent(query.trim())}`).then(setResults).catch(() => setResults(null)), 180);
    return () => window.clearTimeout(timer);
  }, [open, query]);
  useEffect(() => { if (!open) setQuery(""); }, [open]);
  if (!open) return null;
  const entries = results ? [
    ...results.licenses.map((item) => ({ label: `${item.product.name} · ${item.keyPrefix}`, meta: `License · ${item.status}`, to: `/licenses/${item.id}` })),
    ...results.products.map((item) => ({ label: item.name, meta: `Product · ${item.slug}`, to: `/admin/products?select=${item.id}` })),
    ...results.buyers.filter((item) => item.discordUserId).map((item) => ({ label: item.displayName || item.discordUserId!, meta: item.email || "Buyer profile", to: `/admin/buyers/${item.discordUserId}` })),
    ...results.links.map((item) => ({ label: item.buyerUsername, meta: `BuiltByBit · ${item.status}`, to: "/admin/builtbybit" })),
  ] : [];
  return <div className="command-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><section aria-label="Workspace search" className="command-palette" role="dialog"><label><Search size={19} /><input autoFocus onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Escape") onClose(); }} placeholder="Search licenses, buyers, products, or BuiltByBit" value={query} /><kbd>ESC</kbd></label><div className="command-results">{query.trim().length < 2 ? <><span className="command-section">Recent admin pages</span>{recent.length ? recent.map((to) => <button key={to} onClick={() => onNavigate(to)} type="button"><FileClock size={16} /><span><strong>{breadcrumb(to)}</strong><small>{to}</small></span></button>) : <p>Start typing to search the workspace.</p>}</> : entries.length ? entries.map((entry) => <button key={`${entry.to}:${entry.label}`} onClick={() => onNavigate(entry.to)} type="button"><Search size={16} /><span><strong>{entry.label}</strong><small>{entry.meta}</small></span></button>) : <p>No matching records.</p>}</div></section></div>;
}

function NotificationMenu({ items, onChanged }: { items: AdminNotification[]; onChanged(items: AdminNotification[]): void }) {
  async function act(item: AdminNotification, action: "READ" | "RESOLVE") {
    const updated = await api<AdminNotification>(`/v1/web/admin/notifications/${item.id}/action`, { method: "POST", body: jsonBody({ action }) });
    onChanged(items.map((row) => row.id === item.id ? updated : row));
  }
  return <div className="notification-menu"><div className="notification-menu__head"><strong>Operations inbox</strong><span>{items.filter((item) => !item.readAt).length} unread</span></div>{items.length === 0 ? <p>No operational alerts.</p> : items.slice(0, 8).map((item) => <article className={!item.readAt ? "is-unread" : ""} key={item.id}><span className={`notification-severity notification-severity--${item.severity.toLowerCase()}`} /><div><strong>{item.title}</strong><p>{item.message}</p><small>{relativeTime(item.createdAt)}</small></div><button onClick={() => void act(item, item.readAt ? "RESOLVE" : "READ")} type="button">{item.readAt ? "Resolve" : "Read"}</button></article>)}</div>;
}

function breadcrumb(pathname: string) {
  const labels: Record<string, string> = {
    "/admin": "Administration / Operations",
    "/admin/audit": "Administration / Audit logs",
    "/admin/builtbybit": "Administration / BuiltByBit",
    "/admin/buyers": "Administration / Buyers",
    "/admin/discord-panel": "Administration / Discord panel",
    "/admin/licenses": "Administration / Licenses",
    "/admin/operations": "Administration / Discord operations",
    "/admin/products": "Administration / Products",
    "/admin/ticket-panel": "Administration / Ticket panel",
  };
  if (pathname.startsWith("/admin/buyers/")) return "Administration / Buyers / Profile";
  if (pathname.startsWith("/licenses/")) return "Workspace / License detail";
  return labels[pathname] || (pathname.startsWith("/admin") ? "Administration" : "The License Concourse");
}

function ShellNavLink({
  children,
  end = false,
  onClick,
  to,
}: PropsWithChildren<{ end?: boolean; onClick?(): void; to: string }>) {
  const pathname = usePathname();
  const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  return <Link className={`nav-item ${active ? "is-active" : ""}`} onClick={onClick} to={to}>{children}</Link>;
}
